import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { taskService } from '../api/taskService';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import DivisionDashboard from './DivisionDashboard';
import TaskProgress from '../components/TaskProgress';
import EventsProgress from '../components/EventsProgress';
import EmployeeTaskList from '../components/EmployeeTaskList';
import {
    CheckCircle, RefreshCw, PlusCircle, AlertCircle, Briefcase, Clock,
    Calendar as CalendarIcon, Users, Video, MapPin, ChevronDown, ChevronRight, ChevronLeft, User
} from 'lucide-react';
import { eventService } from '../api/eventService';
import { userService } from '../api/userService';

// Import Logos
import RISLogo from '../assets/RIS-LoGO.png';
import AICLogo from '../assets/AIC_logo.png';
import FITMLogo from '../assets/FITM_LOGO.png';
import CMECLogo from '../assets/CMEC_LOGO.png';
import DakshinLogo from '../assets/DAKSHIN_logo.png';
import WeeklyCalendar from '../components/WeeklyCalendar';
import Organogram from '../components/Organogram';
import { DIVISIONS } from '../constants/divisions';

// Mini Monthly Calendar Component
const MiniCalendar = ({ events = [] }) => {
    const today = new Date();
    const [currentDate, setCurrentDate] = useState(new Date());
    const navigate = useNavigate();

    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
    };

    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
        days.push(<div key={`empty-${i}`} style={{ padding: '4px' }}></div>);
    }
    for (let i = 1; i <= daysInMonth; i++) {
        const isToday = i === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();

        // Find events on this day
        const dayEvents = events.filter(e => {
            const dateStr = e.StartDateTime || e.StartDate || e.date;
            if (!dateStr) return false;
            const [y, m, d] = dateStr.split('T')[0].split('-');
            return parseInt(y) === currentYear && parseInt(m) === currentMonth + 1 && parseInt(d) === i;
        });

        let hasEvent = dayEvents.length > 0;
        let hasConflict = false;

        if (dayEvents.length > 1) {
            // Sort by start time to check overlaps
            const sorted = [...dayEvents].sort((a, b) => new Date(a.StartDateTime || a.StartDate).getTime() - new Date(b.StartDateTime || b.StartDate).getTime());
            for (let j = 1; j < sorted.length; j++) {
                const prevEvent = sorted[j - 1];
                const prevEnd = prevEvent.EndDateTime || prevEvent.EndDate || prevEvent.StartDateTime || prevEvent.StartDate;
                const currStart = sorted[j].StartDateTime || sorted[j].StartDate;
                // If current event starts before previous event ends...
                if (new Date(currStart) < new Date(prevEnd)) {
                    hasConflict = true;
                    break;
                }
            }
        }

        const dateQueryStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;

        days.push(
            <div key={`day-${i}`}
                onClick={() => navigate(`/calendar?date=${dateQueryStr}`)}
                style={{
                    padding: '4px',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    color: (isToday || hasEvent) ? 'white' : '#475569',
                    background: hasEvent
                        ? (hasConflict ? 'linear-gradient(135deg, #ef4444, #f87171)' : 'linear-gradient(135deg, #10b981, #34d399)')
                        : (isToday ? '#3b82f6' : 'transparent'),
                    borderRadius: hasEvent ? '6px' : '50%',
                    border: (isToday && hasEvent) ? '2px solid #1e3a8a' : '2px solid transparent',
                    width: '26px', // slightly wider to accommodate borders nicely
                    height: '26px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: 'auto',
                    fontWeight: isToday ? 700 : 500,
                    cursor: 'pointer'
                }}>
                {i}
            </div>
        );
    }

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', color: '#1e293b' }}>
            {/* Calendar Header */}
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                    onClick={handlePrevMonth}
                    style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#1e293b'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#64748b'}
                >
                    <ChevronLeft size={16} />
                </button>
                <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>{monthNames[currentMonth]} {currentYear}</span>
                <button
                    onClick={handleNextMonth}
                    style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#1e293b'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#64748b'}
                >
                    <ChevronRight size={16} />
                </button>
            </div>
            {/* Calendar Body */}
            <div style={{ padding: '0.75rem 1rem 1.5rem 1rem', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
                    {dayNames.map(day => (
                        <div key={day} style={{ textAlign: 'center', fontSize: '0.65rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                            {day}
                        </div>
                    ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                    {days}
                </div>
            </div>
        </div>
    );
};

const DGDashboard = () => {
    const { selectedDivision } = useOutletContext() || {};

    // React Query Hooks
    const { data: allTasks = [] } = useQuery({
        queryKey: ['dg_tasks'],
        queryFn: taskService.getAllTasks,
        staleTime: 60000
    });

    const { data: allEvents = [] } = useQuery({
        queryKey: ['dg_events'],
        queryFn: eventService.getAllEvents, // Assuming getAllEvents exists and works similarly
        staleTime: 60000
    });

    const { data: usersData = [] } = useQuery({
        queryKey: ['dg_users'],
        queryFn: () => userService.getAllUsers().catch(() => []),
        staleTime: 300000
    });

    const allUsers = useMemo(
        () => (Array.isArray(usersData) ? usersData : (usersData?.Users || [])),
        [usersData]
    );

    // Derived State
    const { tasks, events, users } = useMemo(() => {
        let filteredTasks = (allTasks || []).filter(t => (t.Status || t.status || '').toLowerCase() !== 'cancelled');
        let filteredEvents = (allEvents || []).filter(e => (e.Status || e.status || '').toLowerCase() !== 'cancelled');

        if (selectedDivision) {
            const divisionName = selectedDivision.split('/').pop();
            filteredTasks = filteredTasks.filter(t => t.Division === divisionName);
            filteredEvents = filteredEvents.filter(e => e.Division === divisionName);
        }

        return { tasks: filteredTasks, events: filteredEvents, users: allUsers };
    }, [allTasks, allEvents, allUsers, selectedDivision]);


    const [expandedDivision, setExpandedDivision] = useState(null);

    const divisionLogos = {
        '/division/AIC': AICLogo,
        '/division/FITM': FITMLogo,
        '/division/CMEC': CMECLogo,
        '/division/DAKSHIN': DakshinLogo,
        '/division/ADMIN': RISLogo,
        '/division/PUBLICATION': RISLogo
    };

    const currentDivisionLogo = selectedDivision ? divisionLogos[selectedDivision] : null;

    const [dueDays] = useState(7);
    const [outlooksDays, setOutlooksDays] = useState(7);
    const [isPersonalView, setIsPersonalView] = useState(false);

    // KPI Logic
    // KPI Logic
    const now = new Date();
    const isWithinDays = (dateStr, days) => {
        if (!dateStr) return false;
        const d = new Date(dateStr);
        const diffTime = Math.abs(now - d);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= days;
    };

    const isUpcoming = (dateStr, days) => {
        if (!dateStr) return false;
        const d = new Date(dateStr);
        d.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffTime = d - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= days;
    };

    // Task KPIs
    const completed7Days = tasks.filter(t => t.Status === 'Completed' && isWithinDays(t.UpdatedAt, 7)).length;
    const updated7Days = tasks.filter(t => isWithinDays(t.UpdatedAt, 7)).length;
    const created7Days = tasks.filter(t => isWithinDays(t.CreatedAt, 7)).length;
    const dueNextXd = tasks.filter(t => isUpcoming(t.DueDate || t.EndDate, dueDays)).length;

    // Event KPIs
    const eventsNext7Days = events.filter(e => isUpcoming(e.StartDateTime, 7)).length;
    const conferencesUpcoming = events.filter(e => e.EventType === 'Conference' && isUpcoming(e.StartDateTime, 30)).length;
    const meetingsUpcoming = events.filter(e => e.EventType === 'Meeting' && isUpcoming(e.StartDateTime, 7)).length;
    const visitsUpcoming = events.filter(e => e.EventType === 'Visit' && isUpcoming(e.StartDateTime, 14)).length;

    const KPICard = ({ title, count, subtitle, color, linkFilter, icon }) => (
        <Link to={`/tasks?filter=${linkFilter}`} style={{ textDecoration: 'none' }}>
            <div style={{
                background: 'white', padding: '1rem', borderRadius: '12px',
                border: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                position: 'relative', overflow: 'hidden', height: '100%', boxSizing: 'border-box'
            }}>
                <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '3px', background: color, opacity: 0.8
                }}></div>
                <div style={{
                    background: `${color} 15`, color: color, padding: '0.6rem', borderRadius: '10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    {React.cloneElement(icon, { size: 20 })}
                </div>
                <div style={{ textAlign: 'left' }}>
                    <h5 style={{ margin: '0 0 0.15rem 0', color: '#64748b', fontSize: '0.75rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.02em' }}>{title}</h5>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
                        <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#0f172a' }}>{count}</span>
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{subtitle}</span>
                    </div>
                </div>
            </div>
        </Link>
    );

    const DIVISIONS_LIST = DIVISIONS.map(d => d.id);

    const getDivisionStats = (divName) => {
        // Exclude cancelled tasks entirely
        const activeTasks = tasks.filter(t => t.Status !== 'Cancelled');

        const assignedTasks = activeTasks.filter(t => (t.Division || '').toUpperCase() === divName.toUpperCase());

        // Helper to normalize roles
        const getUserRoles = (u) => {
            const rawRoles = u.Roles || u.roles || u.Groups || u.groups || u.Group || u.Profile || u.Attributes?.find(a => a.Name === 'custom:role')?.Value || [];
            const roles = Array.isArray(rawRoles) ? rawRoles : (typeof rawRoles === 'string' ? [rawRoles] : []);
            return roles.map(r => String(r).toUpperCase());
        };

        const coordRole = `COORDINATOR_${divName.toUpperCase()}`;
        const employeeRole = `EMPLOYEE_${divName.toUpperCase()}`;

        const divUsers = users.filter(u => {
            const upperRoles = getUserRoles(u);
            return upperRoles.includes(coordRole) || upperRoles.includes(employeeRole);
        });

        const coordinatorsCount = divUsers.filter(u => getUserRoles(u).includes(coordRole)).length;
        const employeesCount = divUsers.filter(u => getUserRoles(u).includes(employeeRole)).length;

        // Collect names and IDs to properly identify who created what
        const divUserIds = divUsers.map(u => u.sub || u.id || u.Username || u.UserId).filter(Boolean);
        const divUserEmails = divUsers.map(u => u.Email || u.Attributes?.find(a => a.Name === 'email')?.Value).filter(Boolean);
        const divUserNames = divUsers.map(u => u.Name || u.Attributes?.find(a => a.Name === 'name')?.Value).filter(Boolean);

        const createdTasks = activeTasks.filter(t => {
            if (t.CreatedByDivision && t.CreatedByDivision.toUpperCase() === divName.toUpperCase()) return true;

            const byId = t.CreatedByID;
            const byName = t.CreatedBy;

            if (byId && divUserIds.includes(byId)) return true;
            if (byName && (divUserNames.includes(byName) || divUserEmails.includes(byName) || divUserIds.includes(byName))) return true;

            return false;
        });

        // Use Set to find unique total tasks representing the division's footprint
        const uniqueTotalTasksArray = Array.from(new Set([...assignedTasks, ...createdTasks]));

        const assignedCount = assignedTasks.length;
        const createdCount = createdTasks.length;
        const total = uniqueTotalTasksArray.length;

        const completed = uniqueTotalTasksArray.filter(t => t.Status === 'Completed').length;
        const pending = uniqueTotalTasksArray.filter(t => t.Status === 'Pending').length;
        const inProgress = uniqueTotalTasksArray.filter(t => t.Status === 'In Progress').length;

        const efficiency = total > 0 ? Math.round((completed / total) * 100) : 0;

        return {
            created: createdCount,
            assigned: assignedCount,
            completed,
            pending,
            inProgress,
            total,
            efficiency,
            coordinators: coordinatorsCount,
            employees: employeesCount
        };
    };

    const getCoordinatorsInDivision = (divName) => {
        const divTasks = tasks.filter(t => (t.Division || '').toUpperCase() === divName.toUpperCase() && t.Status !== 'Cancelled');
        const coordRole = `COORDINATOR_${divName.toUpperCase()}`;

        // Filter list to strictly show only Coordinators
        const coordinators = users.filter(u => {
            const rawRoles = u.Roles || u.roles || u.Groups || u.groups || u.Group || u.Profile || u.Attributes?.find(a => a.Name === 'custom:role')?.Value || [];
            const roles = Array.isArray(rawRoles) ? rawRoles : (typeof rawRoles === 'string' ? [rawRoles] : []);
            return roles.some(r => String(r).toUpperCase() === coordRole);
        });

        return coordinators.map(user => {
            const id = user.sub || user.Username || user.UserId || user.id;
            const name = user.Name || user.Attributes?.find(a => a.Name === 'name')?.Value || user.Username || id;
            const email = user.Email || user.Attributes?.find(a => a.Name === 'email')?.Value;

            // Only tasks explicitly assigned to them
            const coordAssignedTasks = divTasks.filter(t => {
                const assigneeId = t.AssigneeID || t.AssignedTo;
                const assigneeName = t.AssigneeName;

                return (
                    (assigneeId && assigneeId === id) ||
                    (user.Username && assigneeId === user.Username) ||
                    (email && assigneeId === email) ||
                    (name && assigneeName === name) ||
                    (name && assigneeId === name)
                );
            });

            // Tasks created by them (from global tasks list, checking cancelled status)
            const coordCreatedTasks = tasks.filter(t => {
                if (t.Status === 'Cancelled') return false;
                const byId = t.CreatedByID;
                const byName = t.CreatedBy; // Note: CreatedBy in backend often stores the unique ID

                const exactMatch = [id, name, email, user.Username, user.sub, user.UserId, user.id].filter(Boolean);

                return (byId && exactMatch.includes(byId)) || (byName && exactMatch.includes(byName));
            });

            // Calculate un-duplicated total footprint for the coordinator
            const uniqueTotalTasksArray = Array.from(new Set([...coordAssignedTasks, ...coordCreatedTasks]));

            const assignedCount = coordAssignedTasks.length;
            const createdCount = coordCreatedTasks.length;
            const total = uniqueTotalTasksArray.length;

            const completedCount = uniqueTotalTasksArray.filter(t => t.Status === 'Completed').length;
            const pendingCount = uniqueTotalTasksArray.filter(t => t.Status === 'Pending').length;
            const inProgressCount = uniqueTotalTasksArray.filter(t => t.Status === 'In Progress').length;

            const efficiency = total > 0 ? Math.round((completedCount / total) * 100) : 0;

            return {
                id,
                name,
                createdCount,
                assignedCount,
                completedCount,
                pendingCount,
                inProgressCount,
                total,
                efficiency
            };
        }).sort((a, b) => b.total - a.total);
    };


    const unifiedBoxTitleStyle = {
        fontSize: '0.9rem',
        color: '#0f172a',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 700,
        margin: 0,
        textAlign: 'center',
        width: '100%'
    };

    const taskSectionGradient = 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 55%, #ede9fe 100%)';
    const eventSectionGradient = 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 55%, #ffffff 100%)';
    const eventOutlookGradient = 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 55%, #ffffff 100%)';
    const neutralSectionGradient = 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)';

    const taskTitlePillStyle = {
        ...unifiedBoxTitleStyle,
        color: '#ffffff',
        background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
        borderRadius: '999px',
        padding: '0.45rem 1rem',
        width: 'fit-content',
        margin: '0 auto'
    };

    const eventTitlePillStyle = {
        ...unifiedBoxTitleStyle,
        color: '#ffffff',
        background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
        borderRadius: '999px',
        padding: '0.45rem 1rem',
        width: 'fit-content',
        margin: '0 auto'
    };

    const dgIndividualEvents = useMemo(() => {
        const getUserRoles = (u) => {
            const rawRoles = u.Roles || u.roles || u.Groups || u.groups || u.Group || u.Profile || u.Attributes?.find(a => a.Name === 'custom:role')?.Value || [];
            const roles = Array.isArray(rawRoles) ? rawRoles : (typeof rawRoles === 'string' ? [rawRoles] : []);
            return roles.map(r => String(r).toUpperCase());
        };

        const dgUsers = users.filter(u => {
            const roles = getUserRoles(u);
            return roles.includes('DG') || roles.includes('DG_OFFICE');
        });

        const dgUserIds = dgUsers.map(u => u.sub || u.id || u.Username || u.UserId).filter(Boolean);
        const dgUserEmails = dgUsers.map(u => u.Email || u.Attributes?.find(a => a.Name === 'email')?.Value).filter(Boolean);
        const dgUserNames = dgUsers.map(u => u.Name || u.Attributes?.find(a => a.Name === 'name')?.Value).filter(Boolean);

        return events.filter(e => {
            const isIndividual = e.Scope === 'Individual' || e.scope === 'Individual';
            if (!isIndividual) return false;

            const byId = e.CreatedByID;
            const byName = e.CreatedBy;

            if (byId && dgUserIds.includes(byId)) return true;
            if (byName && (dgUserNames.includes(byName) || dgUserEmails.includes(byName) || dgUserIds.includes(byName))) return true;

            return false;
        });
    }, [events, users]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

            {/* 1. Branding Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <img src={RISLogo} alt="RIS Logo" style={{ height: '60px', objectFit: 'contain' }} />
                    <div style={{ borderLeft: '2px solid #cbd5e1', height: '40px', margin: '0 0.5rem' }}></div>
                    <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>
                        Executive Overview
                    </h1>
                </div>

                {/* Right: Division Logo */}
                {currentDivisionLogo && (
                    <img src={currentDivisionLogo} alt="Division Logo" style={{ height: '50px', objectFit: 'contain' }} />
                )}
            </div>

            {/* Top Analytics: Event Outlook */}
            <div style={{
                background: eventOutlookGradient, padding: '1.6rem', borderRadius: '18px', border: '1px solid #cbd5e1',
                display: 'flex', flexDirection: 'column', gap: '1.1rem', marginBottom: '2rem', overflowX: 'auto',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', top: '-40px', right: '-30px', width: '180px', height: '180px', borderRadius: '999px', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ position: 'absolute', bottom: '-55px', left: '-20px', width: '220px', height: '220px', borderRadius: '999px', background: 'rgba(255,255,255,0.06)' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 10 }}>
                    {/* Personal button — left corner */}
                    <button
                        onClick={() => setIsPersonalView(!isPersonalView)}
                        style={{
                            border: isPersonalView ? 'none' : '1px solid #fb923c',
                            padding: '0.4rem 1.1rem',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            background: isPersonalView ? '#f97316' : 'rgba(255,255,255,0.7)',
                            color: isPersonalView ? '#fff' : '#ea580c',
                            boxShadow: isPersonalView ? '0 1px 3px rgba(249,115,22,0.4)' : '0 1px 2px rgba(0,0,0,0.05)',
                            transition: 'all 0.2s',
                            flexShrink: 0,
                        }}
                    >
                        Personal
                    </button>
                    {/* Title — centered */}
                    <div style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        textAlign: 'center',
                        color: '#0f172a',
                        fontSize: '1rem',
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        pointerEvents: 'none',
                    }}>
                        Event Outlook
                    </div>
                    {/* Day pills — right */}
                    <div style={{ display: 'flex', gap: '0.25rem', background: 'rgba(255,255,255,0.5)', padding: '0.25rem', borderRadius: '8px', border: '1px solid #e2e8f0', width: '120px', justifyContent: 'center' }}>
                        {[3, 5, 7].map(num => (
                            <button
                                key={num}
                                onClick={() => setOutlooksDays(num)}
                                style={{
                                    border: 'none',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '6px',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    background: outlooksDays === num ? '#fff' : 'transparent',
                                    color: outlooksDays === num ? '#0f766e' : '#64748b',
                                    boxShadow: outlooksDays === num ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                    transition: 'all 0.2s',
                                }}
                            >
                                {num}D
                            </button>
                        ))}
                    </div>
                </div>

                <WeeklyCalendar
                    events={isPersonalView ? dgIndividualEvents : events}
                    daysCount={outlooksDays}
                />
            </div>

            {/* Organogram */}
            <div style={{
                background: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 55%, #f5f3ff 100%)',
                borderRadius: '18px',
                border: '1px solid #c7d2fe',
                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.10)',
                padding: '1.6rem',
                marginBottom: '2rem',
                position: 'relative',
                overflow: 'visible',
            }}>
                <div style={{ position: 'absolute', top: '-40px', right: '-30px', width: '200px', height: '200px', borderRadius: '999px', background: 'rgba(99,102,241,0.05)' }} />
                <div style={{ position: 'absolute', bottom: '-50px', left: '-20px', width: '180px', height: '180px', borderRadius: '999px', background: 'rgba(99,102,241,0.04)' }} />
                <div style={{ textAlign: 'center', marginBottom: '0.25rem', position: 'relative', zIndex: 1 }}>
                    <span style={{
                        display: 'inline-block',
                        background: 'linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%)',
                        color: '#fff',
                        borderRadius: '999px',
                        padding: '0.45rem 1.4rem',
                        fontWeight: 800,
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                    }}>Organisational Structure</span>
                </div>
                <Organogram />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '2rem', marginBottom: '2rem', alignItems: 'start' }}>
                <div style={{
                    background: taskSectionGradient,
                    padding: '1.25rem',
                    borderRadius: '16px',
                    border: '1px solid #bfdbfe',
                    boxShadow: '0 8px 20px rgba(37, 99, 235, 0.10)',
                    height: '420px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <h3 style={{ ...taskTitlePillStyle, marginBottom: '1rem', flexShrink: 0 }}>Task Progress</h3>
                    <div style={{ 
                        flex: 1, 
                        overflowY: 'auto', 
                        padding: '1.5rem', 
                        background: 'white', 
                        borderRadius: '12px', 
                        border: '1px solid #eaecf0',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                    }}>
                        <TaskProgress tasks={tasks} />
                    </div>
                </div>
                <div style={{
                    background: eventSectionGradient,
                    padding: '1.25rem',
                    borderRadius: '16px',
                    border: '1px solid #bfdbfe',
                    boxShadow: '0 8px 20px rgba(37, 99, 235, 0.14)',
                    height: '420px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <h3 style={{ ...eventTitlePillStyle, marginBottom: '1rem', flexShrink: 0 }}>Events Progress</h3>
                    <div style={{ 
                        flex: 1, 
                        overflowY: 'auto', 
                        padding: '1.5rem', 
                        background: 'white', 
                        borderRadius: '12px', 
                        border: '1px solid #eaecf0',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                    }}>
                        <EventsProgress events={events.filter(e => e.Scope !== 'Individual' && e.scope !== 'Individual')} />
                    </div>
                </div>
            </div>

            <div style={{
                background: neutralSectionGradient, padding: '1.5rem', borderRadius: '16px', border: '1px solid #dbeafe',
                overflow: 'hidden', height: '500px', display: 'flex', flexDirection: 'column', marginBottom: '2rem'
            }}>
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <h3 style={taskTitlePillStyle}>Division-Wise Drill Down</h3>
                </div>

                <div style={{ overflowY: 'auto', flex: 1, paddingRight: '0.25rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                                <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', color: '#64748b' }}>Division</th>
                                <th style={{ textAlign: 'center', padding: '0.75rem 0.5rem', color: '#8b5cf6' }}>Created</th>
                                <th style={{ textAlign: 'center', padding: '0.75rem 0.5rem', color: '#3b82f6' }}>Assigned</th>
                                <th style={{ textAlign: 'center', padding: '0.75rem 0.5rem', color: '#06b6d4' }}>In Progress</th>
                                <th style={{ textAlign: 'center', padding: '0.75rem 0.5rem', color: '#10b981' }}>Done</th>
                                <th style={{ textAlign: 'center', padding: '0.75rem 0.5rem', color: '#f59e0b' }}>Pending</th>
                                <th style={{ textAlign: 'center', padding: '0.75rem 0.5rem', color: '#94a3b8' }}>Total</th>
                                <th style={{ textAlign: 'center', padding: '0.75rem 0.5rem', color: '#10b981' }}>Efficiency%</th>
                            </tr>
                        </thead>
                        <tbody>
                            {DIVISIONS_LIST.map(div => {
                                const stats = getDivisionStats(div);
                                const isExpanded = expandedDivision === div;

                                return (
                                    <React.Fragment key={div}>
                                        <tr
                                            onClick={() => setExpandedDivision(isExpanded ? null : div)}
                                            style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: isExpanded ? '#f8fafc' : 'transparent' }}
                                            className="hover-bg"
                                        >
                                            <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                {div}
                                            </td>
                                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#8b5cf6' }}>{stats.created}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#3b82f6' }}>{stats.assigned}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#06b6d4' }}>{stats.inProgress}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#10b981' }}>{stats.completed}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#f59e0b' }}>{stats.pending}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#1e293b' }}>{stats.total}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 700, color: stats.efficiency >= 80 ? '#10b981' : stats.efficiency >= 50 ? '#f59e0b' : '#ef4444' }}>{stats.efficiency}%</td>
                                        </tr>
                                        {isExpanded && getCoordinatorsInDivision(div).map(coord => (
                                            <tr key={coord.id} style={{ background: '#fcfcfd', borderBottom: '1px solid #f8fafc' }}>
                                                <td style={{ padding: '0.5rem 0.5rem 0.5rem 1.5rem', color: '#64748b', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                    <User size={12} /> {coord.name}
                                                </td>
                                                <td style={{ textAlign: 'center', color: '#8b5cf6' }}>{coord.createdCount}</td>
                                                <td style={{ textAlign: 'center', color: '#3b82f6' }}>{coord.assignedCount}</td>
                                                <td style={{ textAlign: 'center', color: '#06b6d4' }}>{coord.inProgressCount}</td>
                                                <td style={{ textAlign: 'center', color: '#10b981' }}>{coord.completedCount}</td>
                                                <td style={{ textAlign: 'center', color: '#f59e0b' }}>{coord.pendingCount}</td>
                                                <td style={{ textAlign: 'center', color: '#94a3b8' }}>{coord.total}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 500, color: coord.efficiency >= 80 ? '#10b981' : coord.efficiency >= 50 ? '#f59e0b' : '#ef4444' }}>{coord.efficiency}%</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
                <div style={{
                    background: taskSectionGradient,
                    padding: '1.5rem',
                    borderRadius: '18px',
                    border: '1px solid #bfdbfe',
                    boxShadow: '0 8px 20px rgba(37, 99, 235, 0.12)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.25rem'
                }}>
                    <h3 style={taskTitlePillStyle}>Task Insights</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <KPICard title="Active Tasks" count={tasks.filter(t => t.Status !== 'Completed' && t.Status !== 'Cancelled').length} subtitle="in progress" color="#0f172a" linkFilter="active" icon={<Briefcase />} />
                        <KPICard title="Overdue" count={tasks.filter(t => t.Status !== 'Completed' && t.Status !== 'Cancelled' && t.DueDate && new Date(t.DueDate) < now).length} subtitle="past due" color="#ef4444" linkFilter="overdue" icon={<AlertCircle />} />
                        <KPICard title="Due Soon" count={dueNextXd} subtitle={`next ${dueDays} days`} color="#f59e0b" linkFilter={`due_${dueDays}d`} icon={<Clock />} />
                        <KPICard title="Completed" count={completed7Days} subtitle="last 7d" color="#10b981" linkFilter="completed_7d" icon={<CheckCircle />} />
                        <KPICard title="Updated" count={updated7Days} subtitle="last 7d" color="#3b82f6" linkFilter="updated_7d" icon={<RefreshCw />} />
                        <KPICard title="Created" count={created7Days} subtitle="last 7d" color="#8b5cf6" linkFilter="created_7d" icon={<PlusCircle />} />
                        <KPICard title="Pending Review" count={tasks.filter(t => t.Status === 'Pending').length} subtitle="needs action" color="#f59e0b" linkFilter="pending" icon={<AlertCircle />} />
                    </div>
                </div>

                <div style={{
                    background: eventSectionGradient,
                    padding: '1.5rem',
                    borderRadius: '18px',
                    border: '1px solid #bfdbfe',
                    boxShadow: '0 8px 20px rgba(37, 99, 235, 0.16)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.25rem'
                }}>
                    <h3 style={eventTitlePillStyle}>Events</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <Link to="/events?filter=next7" style={{ textDecoration: 'none' }}>
                            <div style={{ background: 'rgba(255,255,255,0.84)', padding: '1rem', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '0.75rem', position: 'relative', overflow: 'hidden', height: '100%', boxSizing: 'border-box', border: '1px solid rgba(251, 146, 60, 0.3)' }}>
                                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '3px', background: '#8b5cf6', opacity: 0.8 }}></div>
                                <div style={{ background: '#f3e8ff', color: '#8b5cf6', padding: '0.6rem', borderRadius: '10px' }}><CalendarIcon size={20} /></div>
                                <div style={{ textAlign: 'left' }}>
                                    <h5 style={{ margin: '0 0 0.15rem 0', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Next 7 Days</h5>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e293b' }}>{eventsNext7Days}</div>
                                </div>
                            </div>
                        </Link>
                        <Link to="/events?filter=Conference" style={{ textDecoration: 'none' }}>
                            <div style={{ background: 'rgba(255,255,255,0.84)', padding: '1rem', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '0.75rem', position: 'relative', overflow: 'hidden', height: '100%', boxSizing: 'border-box', border: '1px solid rgba(251, 146, 60, 0.3)' }}>
                                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '3px', background: '#3b82f6', opacity: 0.8 }}></div>
                                <div style={{ background: '#dbeafe', color: '#3b82f6', padding: '0.6rem', borderRadius: '10px' }}><Users size={20} /></div>
                                <div style={{ textAlign: 'left' }}>
                                    <h5 style={{ margin: '0 0 0.15rem 0', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Conferences</h5>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e293b' }}>{conferencesUpcoming}</div>
                                </div>
                            </div>
                        </Link>
                        <Link to="/events?filter=Meeting" style={{ textDecoration: 'none' }}>
                            <div style={{ background: 'rgba(255,255,255,0.84)', padding: '1rem', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '0.75rem', position: 'relative', overflow: 'hidden', height: '100%', boxSizing: 'border-box', border: '1px solid rgba(251, 146, 60, 0.3)' }}>
                                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '3px', background: '#10b981', opacity: 0.8 }}></div>
                                <div style={{ background: '#d1fae5', color: '#10b981', padding: '0.6rem', borderRadius: '10px' }}><Video size={20} /></div>
                                <div style={{ textAlign: 'left' }}>
                                    <h5 style={{ margin: '0 0 0.15rem 0', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Meetings</h5>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e293b' }}>{meetingsUpcoming}</div>
                                </div>
                            </div>
                        </Link>
                        <Link to="/events?filter=Visit" style={{ textDecoration: 'none' }}>
                            <div style={{ background: 'rgba(255,255,255,0.84)', padding: '1rem', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '0.75rem', position: 'relative', overflow: 'hidden', height: '100%', boxSizing: 'border-box', border: '1px solid rgba(251, 146, 60, 0.3)' }}>
                                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '3px', background: '#f59e0b', opacity: 0.8 }}></div>
                                <div style={{ background: '#fef3c7', color: '#f59e0b', padding: '0.6rem', borderRadius: '10px' }}><MapPin size={20} /></div>
                                <div style={{ textAlign: 'left' }}>
                                    <h5 style={{ margin: '0 0 0.15rem 0', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Visits</h5>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e293b' }}>{visitsUpcoming}</div>
                                </div>
                            </div>
                        </Link>
                    </div>
                    <div style={{
                        background: 'rgba(255,255,255,0.78)',
                        padding: '0.9rem',
                        borderRadius: '14px',
                        border: '1px solid rgba(251, 146, 60, 0.25)'
                    }}>
                        <MiniCalendar events={events} />
                    </div>
                </div>
            </div>
        </div>
    );
};

// ... existing Employee View components ...

const TaskOverview = ({ tasks }) => {
    // Logic: Count Total Assigned and Due Tasks by Priority
    const totalAssigned = tasks.length;
    const dueTasks = tasks.filter(t => t.Status !== 'Completed');
    const high = dueTasks.filter(t => t.Priority === 'High').length;
    const medium = dueTasks.filter(t => t.Priority === 'Medium').length;
    const low = dueTasks.filter(t => t.Priority === 'Low').length;

    const StatCard = ({ label, value, color, icon }) => (
        <div className="modern-card" style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: '1rem',
            minWidth: '140px', border: 'none', background: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px',
            position: 'relative', overflow: 'hidden'
        }}>
            <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '4px', background: color }}></div>
            <div style={{
                padding: '0.75rem', borderRadius: '50%', background: `${color} 15`, color: color,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
                {icon}
            </div>
            <div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem', fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#333' }}>{value}</div>
            </div>
        </div>
    );

    return (
        <div style={{ marginBottom: '2rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <StatCard label="Total Assigned" value={totalAssigned} color="#007bff" icon={<Briefcase size={20} />} />
            <StatCard label="High Priority Due" value={high} color="#dc3545" icon={<AlertCircle size={20} />} />
            <StatCard label="Medium Priority Due" value={medium} color="#ffc107" icon={<Clock size={20} />} />
            <StatCard label="Low Priority Due" value={low} color="#28a745" icon={<CheckCircle size={20} />} />
        </div>
    );
};

const EmployeeDashboard = () => {
    const { data: rawTasks = [] } = useQuery({
        queryKey: ['employee_tasks'],
        queryFn: taskService.getAll,
    });

    const tasks = useMemo(() => 
        (rawTasks || []).filter(t => (t.Status || t.status || '').toLowerCase() !== 'cancelled'),
        [rawTasks]
    );


    return (
        <div>
            <TaskOverview tasks={tasks} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                <div className="modern-card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
                    <EmployeeTaskList tasks={tasks} />
                </div>
            </div>
        </div>
    );
};

const Dashboard = () => {
    const { user } = useAuth();
    const roles = user?.roles || [];

    if (roles.includes('DG') || roles.includes('DG_OFFICE')) {
        return <DGDashboard />;
    }

    const coordinatorRole = roles.find(r => r.startsWith('COORDINATOR_'));
    if (coordinatorRole) {
        const division = coordinatorRole.split('_')[1];
        return <DivisionDashboard division={division} />;
    }

    return <EmployeeDashboard />;
};

export default Dashboard;
